import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { ChargeService } from 'src/app/services/charge/charge.service';
import { InspectionChargesModalService } from 'src/app/services/inspection-charges-modal/inspection-charges-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-inspection-charges',
  templateUrl: './inspection-charges.component.html',
  styleUrls: ['./inspection-charges.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate('300ms ease-in', style({ transform: 'translateY(0%)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ transform: 'translateY(-100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class InspectionChargesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  inspectionCharges: any[] = [];
  isLoading = false;
  type = "inspection"
  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedInspectionCharges: string[] = [];

  constructor(
    private _chargeService: ChargeService,
    private _inspectionChargesModalService: InspectionChargesModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadInspectionCharges();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadInspectionCharges(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedInspectionCharges = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    // Add type filter for inspection charges
    const inspectionFilters = { ...filters, type: 'inspection' };

    this.subscriptions.push(
      this._chargeService.GetData(page.toString(), search, inspectionFilters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status) {
            this.inspectionCharges = response.data.data || [];
            this.updatePaginationInfo(response.data.pagination);
          } else {
            this.inspectionCharges = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading inspection charges:', error);
          this.inspectionCharges = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  private updatePaginationInfo(pagination: any): void {
    this.paginationInfo = {
      currentPage: pagination.current_page || 1,
      perPage: pagination.per_page || 10,
      total: pagination.total || 0,
      lastPage: pagination.last_page || 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    this.loadInspectionCharges(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadInspectionCharges(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadInspectionCharges();
  }

  openAddInspectionChargeModal(): void {
    this._inspectionChargesModalService.clearSelectedInspectionCharge();
    const modalElement = document.getElementById('kt_modal_inspection_charge_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(inspectionCharge: any): void {
    this._inspectionChargesModalService.setSelectedInspectionCharge(inspectionCharge);
    const modalElement = document.getElementById('kt_modal_inspection_charge_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteInspectionCharge(inspectionCharge: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${inspectionCharge.name || 'this inspection charge'}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.subscriptions.push(
          this._chargeService.DeleteDataById(inspectionCharge._id).subscribe({
            next: (response) => {
              if (response.status) {
                Swal.fire('Deleted!', 'Inspection charge has been deleted.', 'success');
                this.loadInspectionCharges(this.currentPage, this.searchTerm, this.filters);
              } else {
                Swal.fire('Error!', response.message || 'Failed to delete inspection charge.', 'error');
              }
            },
            error: (error) => {
              console.error('Error deleting inspection charge:', error);
              Swal.fire('Error!', 'An error occurred while deleting the inspection charge.', 'error');
            }
          })
        );
      }
    });
  }

  deleteSelectedInspectionCharges(): void {
    if (this.selectedInspectionCharges.length === 0) {
      Swal.fire('Warning!', 'Please select inspection charges to delete.', 'warning');
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${this.selectedInspectionCharges.length} inspection charge(s). This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!'
    }).then((result) => {
      if (result.isConfirmed) {
        let deletedCount = 0;
        const totalCount = this.selectedInspectionCharges.length;

        this.selectedInspectionCharges.forEach(id => {
          this.subscriptions.push(
            this._chargeService.DeleteDataById(id).subscribe({
              next: () => {
                deletedCount++;
                if (deletedCount === totalCount) {
                  Swal.fire('Deleted!', 'Selected inspection charges have been deleted.', 'success');
                  this.loadInspectionCharges(this.currentPage, this.searchTerm, this.filters);
                }
              },
              error: (error) => {
                console.error('Error deleting inspection charge:', error);
                deletedCount++;
                if (deletedCount === totalCount) {
                  Swal.fire('Error!', 'Some inspection charges could not be deleted.', 'error');
                  this.loadInspectionCharges(this.currentPage, this.searchTerm, this.filters);
                }
              }
            })
          );
        });
      }
    });
  }

  // Event handlers for modal events
  onInspectionChargeAdded(): void {
    console.log('Inspection charge added, refreshing list...');
    this.loadInspectionCharges(this.currentPage, this.searchTerm, this.filters);
  }

  onInspectionChargeUpdated(): void {
    console.log('Inspection charge updated, refreshing list...');
    this.loadInspectionCharges(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(id: string): boolean {
    return this.selectedInspectionCharges.includes(id);
  }

  toggleSelection(id: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      if (!this.selectedInspectionCharges.includes(id)) {
        this.selectedInspectionCharges.push(id);
      }
    } else {
      this.selectedInspectionCharges = this.selectedInspectionCharges.filter(selectedId => selectedId !== id);
    }
    this.updateToolbarVisibility();
  }

  toggleAllSelection(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.selectedInspectionCharges = this.inspectionCharges.map(item => item._id);
    } else {
      this.selectedInspectionCharges = [];
    }
    this.updateToolbarVisibility();
  }

  isAllSelected(): boolean {
    return this.inspectionCharges.length > 0 && this.selectedInspectionCharges.length === this.inspectionCharges.length;
  }

  isIndeterminate(): boolean {
    return this.selectedInspectionCharges.length > 0 && this.selectedInspectionCharges.length < this.inspectionCharges.length;
  }

  private updateToolbarVisibility(): void {
    const baseToolbar = document.querySelector('[data-kt-inspection-charge-table-toolbar="base"]');
    const selectedToolbar = document.querySelector('[data-kt-inspection-charge-table-toolbar="selected"]');

    if (this.selectedInspectionCharges.length > 0) {
      baseToolbar?.classList.add('d-none');
      selectedToolbar?.classList.remove('d-none');
    } else {
      baseToolbar?.classList.remove('d-none');
      selectedToolbar?.classList.add('d-none');
    }
  }
}
